import type { AbnormalReport } from '../api/types';

/**
 * Client mirror of apps-script/Thresholds.gs — used for the offline abnormal check.
 * `rows` are THRESHOLDS sheet rows as delivered in the bootstrap snapshot
 * (keys are the sheet header names).
 */
type Row = Record<string, unknown>;

const s = (v: unknown) => String(v ?? '').trim();
const n = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const x = Number(v);
  return Number.isNaN(x) ? null : x;
};

function classify(v: number, t: Row): 'normal' | 'warning' | 'critical' {
  const cMin = n(t['Critical Min']), cMax = n(t['Critical Max']);
  if ((cMin !== null && v < cMin) || (cMax !== null && v > cMax)) return 'critical';
  const wMin = n(t['Warn Min']), wMax = n(t['Warn Max']);
  if ((wMin !== null && v < wMin) || (wMax !== null && v > wMax)) return 'warning';
  const nMin = n(t['Normal Min']), nMax = n(t['Normal Max']);
  if ((nMin !== null && v < nMin) || (nMax !== null && v > nMax)) return 'warning';
  return 'normal';
}

export interface ResolvedThreshold {
  normalMin: number | null; normalMax: number | null;
  warnMin: number | null; warnMax: number | null;
  critMin: number | null; critMax: number | null;
}

/** Resolve the effective threshold for one field (equipment scope overrides template scope). */
export function resolveThreshold(
  rows: Row[], equipmentId: string, templateId: string, fieldKey: string,
): ResolvedThreshold | null {
  let row: Row | undefined;
  for (const r of rows) if (s(r['Scope']).toLowerCase() === 'template' && s(r['Scope ID']) === templateId && s(r['Field Key']) === fieldKey) row = r;
  for (const r of rows) if (s(r['Scope']).toLowerCase() === 'equipment' && s(r['Scope ID']) === equipmentId && s(r['Field Key']) === fieldKey) row = r;
  if (!row) return null;
  const t: ResolvedThreshold = {
    normalMin: n(row['Normal Min']), normalMax: n(row['Normal Max']),
    warnMin: n(row['Warn Min']), warnMax: n(row['Warn Max']),
    critMin: n(row['Critical Min']), critMax: n(row['Critical Max']),
  };
  return Object.values(t).some((x) => x !== null) ? t : null;
}

/** Severity of a single value against a resolved threshold. */
export function severityOf(v: number, t: ResolvedThreshold | null | undefined): 'normal' | 'warning' | 'critical' {
  if (!t) return 'normal';
  if ((t.critMin !== null && v < t.critMin) || (t.critMax !== null && v > t.critMax)) return 'critical';
  if ((t.warnMin !== null && v < t.warnMin) || (t.warnMax !== null && v > t.warnMax)) return 'warning';
  if ((t.normalMin !== null && v < t.normalMin) || (t.normalMax !== null && v > t.normalMax)) return 'warning';
  return 'normal';
}

export function evaluateThresholds(
  rows: Row[],
  equipmentId: string,
  templateId: string,
  values: Record<string, number | string>,
): AbnormalReport {
  const byField: Record<string, Row> = {};
  for (const r of rows) if (s(r['Scope']).toLowerCase() === 'template' && s(r['Scope ID']) === templateId) byField[s(r['Field Key'])] = r;
  for (const r of rows) if (s(r['Scope']).toLowerCase() === 'equipment' && s(r['Scope ID']) === equipmentId) byField[s(r['Field Key'])] = r;

  const fields: AbnormalReport['fields'] = [];
  let worst: AbnormalReport['status'] = 'normal';
  for (const [key, raw] of Object.entries(values)) {
    const t = byField[key];
    if (!t) continue;
    const v = Number(raw);
    if (Number.isNaN(v)) continue;
    const sev = classify(v, t);
    if (sev !== 'normal') {
      fields.push({ field: key, value: v, severity: sev, normalMin: n(t['Normal Min']), normalMax: n(t['Normal Max']) });
      if (sev === 'critical') worst = 'critical';
      else if (worst === 'normal') worst = 'warning';
    }
  }
  return { status: worst, fields };
}
